import { computed } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { autoinject, inject } from '../Utils/AutoInject';
import { Contract } from '../Utils/Contract';
import { toTimezoneAndFormat } from '../Utils/Date';
import { Enums } from '../Utils/Enums';
import { CardItem } from './CardItem';
import { BsCheck } from '@react-icons/all-files/bs/BsCheck';
import { BsXCircle } from '@react-icons/all-files/bs/BsXCircle';
import {addMonths} from "date-fns";

export interface IContractInfoProps {
    readonly contract: Contract | undefined;
    readonly display?: { shippedDate?: Date };
}

@autoinject
@observer
export class ContractInfo extends React.Component<IContractInfoProps> {
    public render() {
        return (
            <div className="row">
                <div className="col-xl-6">
                    <CardItem
                        label="Contract Status"
                        value={
                            <span className={this.inWarranty ? 'text-success' : 'text-danger'}>
                                {this.contract?.contractStatusMessage(this.props.display?.shippedDate)}
                            </span>
                        }
                    />
                    
                    {this.contract?.warranty && <CardItem label="Warranty" value={this.contract?.warranty} />}

                    {this.contract?.serviceCO && <CardItem label="Service CO" value={this.contract?.serviceCO} />}
                    
                    <CardItem label="Contract Number" value={this.contract?.contractNumber} />
                    
                    {this.account && <CardItem label="Account Name" value={this.account.accountName} />}

                    <CardItem label="Account Number" value={this.contract?.accountNumber} />
                    
                    {this.contract?.isTermBased && (
                        <CardItem label="Term" value={!!this.contract?.term ? this.contract?.term + ' Months' : '-'} />
                    )}
                    
                    <CardItem
                        label="Start Date"
                        value={
                            <span>
                                {this.props.contract?.startDate
                                    ? toTimezoneAndFormat(this.props.contract?.startDate, 'MMMM d, yyyy', 'UTC')
                                    : 'Not Available'}
                            </span>
                        }
                    />

                    <CardItem
                        label="End Date"
                        value={
                            <span>
                                {this.props.contract?.endDate
                                    ? toTimezoneAndFormat(this.props.contract?.endDate, 'MMMM d, yyyy', 'UTC')
                                    : 'Not Available'}
                            </span>
                        }
                    />

                    {!this.props.display && (
                        <>
                            <CardItem
                                label="First Ship Date"
                                value={
                                    <span>
                                        {this.props.contract?.firstShipmentDate
                                            ? toTimezoneAndFormat(
                                                this.props.contract?.firstShipmentDate,
                                                'MMMM d, yyyy',
                                                'UTC'
                                            )
                                            : 'Not Available'}
                                    </span>
                                }
                            />

                            <CardItem
                                label="Last Ship Date"
                                value={
                                    <span>
                                        {this.props.contract?.lastShipmentDate
                                            ? toTimezoneAndFormat(
                                                this.props.contract?.lastShipmentDate,
                                                'MMMM d, yyyy',
                                                'UTC'
                                            )
                                            : 'Not Available'}
                                    </span>
                                }
                            />
                        </>
                    )}

                    {this.props.display && (
                        <CardItem
                            label="Display Ship Date"
                            value={
                                <span>
                                    {this.props.display?.shippedDate
                                        ? toTimezoneAndFormat(this.props.display.shippedDate, 'MMMM d, yyyy', 'UTC')
                                        : 'Not Available'}
                                </span>
                            }
                        />
                    )}

                    {this.contract && this.props.display && this.props.display.shippedDate && 
                        <>
                            <CardItem
                                label="Display Start Date (Ship Date)"
                                value={toTimezoneAndFormat(this.props.display.shippedDate, 'MMMM d, yyyy', 'UTC')}
                            />

                            <CardItem
                                label="Display End Date (Ship Date + Term)"
                                value={
                                    <span className={this.inWarranty ? '' : 'text-danger'}>
                                {toTimezoneAndFormat(addMonths(this.props.display.shippedDate, this.contract.term), 'MMMM d, yyyy', 'UTC')}
                            </span>
                                }
                            />
                        </>
                    }

                    {this.contract?.coveredProduct && (
                        <CardItem label="Covered Product" value={this.contract?.coveredProduct} />
                    )}

                    {this.contract?.technicalSupport && (
                        <CardItem label="Technical Support" value={this.contract?.technicalSupport} />
                    )}

                    {this.contract?.fruReplacements && (
                        <CardItem label="Field Replaceable Units" value={this.contract?.fruReplacements} />
                    )}
                    
                    {this.contract?.nonFruReplacements && (
                        <CardItem label="Non Field Replaceable Units" value={this.contract?.nonFruReplacements} />
                    )}
                    
                    {this.contract?.fieldServicesProvider && (
                        <CardItem label="Field Services Provider" value={this.contract?.fieldServicesProvider} />
                    )}
                </div>
                <div className="col-xl-6">
                    <CardItem
                        label="Service Level"
                        value={Enums.toString(Enums.ServiceLevel, this.contract?.serviceLevel)}
                    />
                    
                    {(!this.props.display || this.managedService) && (
                        <CardItem
                            label="Monitoring and Managed Services"
                            value={this.checkClass(this.managedService)}
                        />
                    )}

                    {(!this.props.display || this.portalAccess) && (
                        <CardItem label="Customer Portal Access" value={this.checkClass(this.portalAccess)} />
                    )}

                    {(!this.props.display || this.contract?.firmwareUpdates) && (
                        <CardItem
                            label="Managed Firmware Updates"
                            value={this.checkClass(this.contract?.firmwareUpdates)}
                        />
                    )}

                    {(!this.props.display || this.contract?.onsiteFieldServices) && (
                        <CardItem
                            label="On Site Field Services"
                            value={this.checkClass(this.contract?.onsiteFieldServices)}
                        />
                    )}

                    {(!this.props.display || this.contract?.m2mDevice) && (
                        <CardItem label="M2M Device" value={this.checkClass(this.contract?.m2mDevice)} />
                    )}

                    {(!this.props.display || this.contract?.customerOwnedParts) && (
                        <CardItem
                            label="Customer Owned Parts"
                            value={this.checkClass(this.contract?.customerOwnedParts)}
                        />
                    )}

                    {(!this.props.display || this.contract?.eventManagement) && (
                        <CardItem label="Event Management" value={this.checkClass(this.contract?.eventManagement)} />
                    )}

                    {(!this.props.display || this.isDemo) && (
                        <CardItem label="Demo Contract" value={this.checkClass(this.isDemo)} />
                    )}

                    {(!this.props.display || this.contract?.advancedWarrantyAlerts) && (
                        <CardItem
                            label="Advanced Warranty Alerts"
                            value={this.checkClass(this.contract?.advancedWarrantyAlerts)}
                        />
                    )}

                    {this.contract?.contractNotes && <CardItem label="Notes" value={this.contract?.contractNotes} />}
                </div>
            </div>
        );
    }

    @computed
    private get inWarranty() {
        return this.contract?.inWarranty(this.props.display?.shippedDate) && this.contract.active;
    }

    @computed
    private get portalAccess() {
        return (
            this.contract?.customerPortal ||
            this.contract?.advancedMonitoring ||
            this.contract?.api ||
            this.contract?.proofOfPlay
        );
    }

    @computed
    private get managedService() {
        return this.contract?.managedServices || this.contract?.monitoringManagedServices;
    }

    @computed
    private get isDemo() {
        return this.contract?.managedServices || this.contract?.monitoringManagedServices;
    }

    @computed
    private get contract() {
        if (this.props.contract) return this.contractsStore.contracts.get(this.props.contract.serviceCO);
    }

    @computed
    private get account() {
        if (this.props.contract?.accountNumber)
            return this.accountsStore.accounts.get(this.props.contract.accountNumber);
    }

    private checkClass(input: boolean | undefined) {
        if (input) return <BsCheck color="teal" />;
        else return <BsXCircle />;
    }

    @inject
    private contractsStore!: { contracts: Map<string, Contract> };
    @inject
    private accountsStore!: { accounts: Map<string, Mri.Accounts.Account> };
}
